/**
 * desktopConfig — non-interactive config dump/set for Zelari Desktop
 * and scripts. Never prints API keys.
 *
 * Flags (handled in main.ts before TUI):
 *   --print-config
 *   --set-config [--provider] [--model] [--endpoint] [--endpoint-clear] [--api-style chat|responses]
 *   --set-key --provider <id> --key <secret>
 *   --discover-models [--provider <id>]
 */

import {
  PROVIDERS,
  resolveApiKey,
  getKeyStorePath,
  getOAuthToken,
  isOAuthProvider,
  setApiKey,
  maskKey,
  type ProviderName,
} from './keyStore.js';
import {
  getProviderConfig,
  getProviderConfigPath,
  setActiveProviderId,
  setModelForProvider,
  setThinkingForProvider,
  setCustomEndpoint,
  clearCustomEndpoint,
  getApiStyleFor,
  setApiStyleFor,
  setKrakenVerifier,
  clearKrakenVerifier,
  getCustomEndpoint,
  type ProviderConfig,
} from './providerConfig.js';
import {
  getCachedModels,
  getStaticFallbackModels,
  discoverModelsForProvider,
  type ProviderId as DiscoveryProviderId,
} from './modelDiscovery.js';
import { getCurrentVersion } from './updater.js';
import {
  parseThinkingSpec,
  isValidThinkingInput,
  thinkingCapabilityFor,
} from './thinking.js';

export interface DesktopProviderInfo {
  id: string;
  displayName: string;
  hasKey: boolean;
  envVar: string;
  models: string[];
  defaultModel: string;
  /** Custom base URL override if set. */
  endpoint?: string | null;
  /** Endpoint style for OpenAI-compatible transports. Present only on
   *  providers where the style is selectable (not anthropic/chatgpt). */
  apiStyle?: 'chat' | 'responses';
  /** Effective base URL (custom or builtin). */
  baseUrl?: string | null;
  /** How the stored credential was obtained. */
  authKind?: 'none' | 'api_key' | 'oauth';
  /** Epoch ms when the OAuth access token expires. */
  expiresAt?: number | null;
  hasRefreshToken?: boolean;
  oauthSupported?: boolean;
  /** Current thinking-effort spec (canonical string). */
  thinking?: string;
  /** Which thinking controls this provider supports. */
  thinkingCapability?: { effort?: boolean; budget?: boolean; efforts?: Array<'low' | 'medium' | 'high' | 'xhigh' | 'max'> };
}

export interface DesktopConfigSnapshot {
  /**
   * Kraken selection verifier override (Fase 9). null = inherit
   * (same as current model — the default and the recommendation).
   */
  activeProviderId: string;
  modelByProvider: Record<string, string>;
  providers: DesktopProviderInfo[];
  krakenVerifier: { provider: string; model: string } | null;
  cliVersion: string;
  configPaths: {
    provider: string;
    keys: string;
  };
}

/** True when argv requests --print-config. */
export function wantsPrintConfig(argv: readonly string[]): boolean {
  return argv.includes('--print-config');
}

export function wantsSetKey(argv: readonly string[]): boolean {
  return argv.includes('--set-key');
}

export function wantsDiscoverModels(argv: readonly string[]): boolean {
  return argv.includes('--discover-models');
}

export function wantsLoginOAuth(argv: readonly string[]): boolean {
  return argv.includes('--login-oauth');
}

export function wantsRefreshOAuth(argv: readonly string[]): boolean {
  return argv.includes('--refresh-oauth');
}

export function wantsLogoutOAuth(argv: readonly string[]): boolean {
  return argv.includes('--logout-oauth');
}

export interface LoginOAuthRequest {
  provider: string;
  code?: string;
  noBrowser?: boolean;
}

export function parseLoginOAuthFlags(argv: readonly string[]): {
  request: LoginOAuthRequest | null;
  error?: string;
} {
  if (!argv.includes('--login-oauth')) return { request: null };
  let provider: string | undefined;
  let code: string | undefined;
  let noBrowser = false;
  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (arg === '--provider') {
      provider = argv[i + 1];
      i++;
    } else if (arg === '--code') {
      code = argv[i + 1];
      i++;
    } else if (arg === '--no-browser') {
      noBrowser = true;
    }
  }
  if (!provider || provider.trim().length === 0) {
    return { request: null, error: '--login-oauth requires --provider <id>' };
  }
  return {
    request: {
      provider: provider.trim(),
      code: code?.trim() || undefined,
      noBrowser: noBrowser || undefined,
    },
  };
}

export function parseProviderOnlyFlag(
  argv: readonly string[],
  flag: '--refresh-oauth' | '--logout-oauth',
): { provider: string | null; error?: string; present: boolean } {
  if (!argv.includes(flag)) return { provider: null, present: false };
  let provider: string | undefined;
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] === '--provider') {
      provider = argv[i + 1];
      i++;
    }
  }
  if (!provider || provider.trim().length === 0) {
    return { provider: null, present: true, error: `${flag} requires --provider <id>` };
  }
  return { provider: provider.trim(), present: true };
}

export interface SetConfigRequest {
  provider?: string;
  model?: string;
  endpoint?: string;
  endpointClear?: boolean;
  /** Endpoint style for OpenAI-compatible providers (chat or responses). */
  apiStyle?: 'chat' | 'responses';
  /** Kraken verifier override — both fields required together. */
  verifierProvider?: string;
  verifierModel?: string;
  /** Remove the override (back to inherit). */
  verifierClear?: boolean;
  thinking?: string;
}

export interface SetConfigParseResult {
  /** null when --set-config is not present */
  request: SetConfigRequest | null;
  error?: string;
}

/**
 * Parse --set-config [--provider id] [--model name] [--endpoint url] [--endpoint-clear].
 */
export function parseSetConfigFlags(argv: readonly string[]): SetConfigParseResult {
  if (!argv.includes('--set-config')) {
    return { request: null };
  }

  let provider: string | undefined;
  let model: string | undefined;
  let endpoint: string | undefined;
  let thinking: string | undefined;
  let apiStyle: string | undefined;
  let endpointClear = false;
  let verifierProvider: string | undefined;
  let verifierModel: string | undefined;
  let verifierClear = false;

  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (arg === '--provider') {
      provider = argv[i + 1];
      i++;
    } else if (arg === '--model') {
      model = argv[i + 1];
      i++;
    } else if (arg === '--endpoint') {
      endpoint = argv[i + 1];
      i++;
    } else if (arg === '--thinking') {
      thinking = argv[i + 1];
      i++;
    } else if (arg === '--api-style') {
      apiStyle = argv[i + 1];
      i++;
    } else if (arg === '--endpoint-clear') {
      endpointClear = true;
    } else if (arg === '--verifier-provider') {
      verifierProvider = argv[i + 1];
      i++;
    } else if (arg === '--verifier-model') {
      verifierModel = argv[i + 1];
      i++;
    } else if (arg === '--verifier-clear') {
      verifierClear = true;
    }
  }

  if (!provider && !model && !endpoint && !endpointClear && !thinking && !apiStyle
    && !verifierProvider && !verifierModel && !verifierClear) {
    return {
      request: null,
      error:
        '--set-config: nothing to update — provide at least one of --provider, --model, --endpoint, --thinking, --verifier-provider + --verifier-model, --verifier-clear, or --endpoint-clear',
    };
  }

  if (provider !== undefined && provider.trim().length === 0) {
    return { request: null, error: '--provider cannot be empty' };
  }
  if (model !== undefined && model.trim().length === 0) {
    return { request: null, error: '--model cannot be empty' };
  }
  if (endpoint !== undefined && endpoint.trim().length === 0) {
    return { request: null, error: '--endpoint cannot be empty' };
  }
  if (apiStyle !== undefined && apiStyle !== 'chat' && apiStyle !== 'responses') {
    return { request: null, error: 'invalid --api-style ' + apiStyle + ' (use chat or responses)' };
  }
  if (verifierClear && (verifierProvider || verifierModel)) {
    return { request: null, error: '--verifier-clear conflicts with --verifier-provider/--verifier-model' };
  }
  if (
    (verifierProvider !== undefined && verifierModel === undefined) ||
    (verifierProvider === undefined && verifierModel !== undefined)
  ) {
    return { request: null, error: '--verifier-provider and --verifier-model must be used together' };
  }
  if (verifierProvider !== undefined && verifierProvider.trim().length === 0) {
    return { request: null, error: '--verifier-provider cannot be empty' };
  }
  if (verifierModel !== undefined && verifierModel.trim().length === 0) {
    return { request: null, error: '--verifier-model cannot be empty' };
  }
  if (endpoint && endpointClear) {
    return { request: null, error: '--endpoint and --endpoint-clear conflict' };
  }
  if (thinking !== undefined && !isValidThinkingInput(thinking)) {
    return { request: null, error: `invalid --thinking value '${thinking}'` };
  }

  return {
    request: {
      provider: provider?.trim(),
      model: model?.trim(),
      endpoint: endpoint?.trim(),
      endpointClear: endpointClear || undefined,
      apiStyle: apiStyle as 'chat' | 'responses' | undefined,
      thinking: thinking?.trim().toLowerCase(),
      verifierProvider: verifierProvider?.trim(),
      verifierModel: verifierModel?.trim(),
      verifierClear: verifierClear || undefined,
    },
  };
}

export interface SetKeyRequest {
  provider: string;
  key: string;
}

export interface SetKeyParseResult {
  request: SetKeyRequest | null;
  error?: string;
}

export function parseSetKeyFlags(argv: readonly string[]): SetKeyParseResult {
  if (!argv.includes('--set-key')) {
    return { request: null };
  }

  let provider: string | undefined;
  let key: string | undefined;

  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (arg === '--provider') {
      provider = argv[i + 1];
      i++;
    } else if (arg === '--key') {
      key = argv[i + 1];
      i++;
    }
  }

  if (!provider || provider.trim().length === 0) {
    return { request: null, error: '--set-key requires --provider <id>' };
  }
  if (!key || key.trim().length === 0) {
    return { request: null, error: '--set-key requires --key <secret>' };
  }

  return {
    request: {
      provider: provider.trim(),
      key: key.trim(),
    },
  };
}

export interface DiscoverModelsParseResult {
  /** null when flag absent */
  provider: string | null;
  error?: string;
  present: boolean;
}

export function parseDiscoverModelsFlags(
  argv: readonly string[],
): DiscoverModelsParseResult {
  if (!argv.includes('--discover-models')) {
    return { provider: null, present: false };
  }

  let provider: string | undefined;
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] === '--provider') {
      provider = argv[i + 1];
      i++;
    }
  }

  return {
    present: true,
    provider: provider?.trim() || null,
  };
}

/** Build config snapshot for desktop/settings (no secrets). */
export function buildDesktopConfigSnapshot(): DesktopConfigSnapshot {
  const config: ProviderConfig = getProviderConfig();
  const providers: DesktopProviderInfo[] = PROVIDERS.map((p) => {
    const cached = getCachedModels(p.id as never);
    const models = cached?.models.map((m) => m.id) ?? [];
    if (models.length === 0) {
      for (const m of getStaticFallbackModels(p.id as DiscoveryProviderId)) {
        if (!models.includes(m.id)) models.push(m.id);
      }
    }
    const defaultModel = config.modelByProvider[p.id] ?? '';
    if (defaultModel && !models.includes(defaultModel)) {
      models.unshift(defaultModel);
    }
    const custom = getCustomEndpoint(p.id as ProviderName);
    const builtin = p.baseUrl ?? null;
    const stored = getOAuthToken(p.id);
    const hasKey = !!resolveApiKey(p.id);
    const oauth = Boolean(stored?.refreshToken || stored?.expiresAt);
    return {
      id: p.id,
      displayName: p.displayName,
      hasKey,
      envVar: p.envVar,
      models,
      defaultModel,
      endpoint: custom ?? null,
      apiStyle:
        p.id === 'anthropic' || p.id === 'chatgpt'
          ? undefined
          : getApiStyleFor(p.id as ProviderName),
      baseUrl: custom ?? builtin,
      authKind: !hasKey ? 'none' : oauth ? 'oauth' : 'api_key',
      expiresAt: stored?.expiresAt ?? null,
      hasRefreshToken: Boolean(stored?.refreshToken),
      oauthSupported: isOAuthProvider(p.id),
      thinking: config.thinkingByProvider[p.id] ?? 'auto',
      thinkingCapability: thinkingCapabilityFor(p.id, defaultModel),
    };
  });

  return {
    activeProviderId: config.activeProviderId,
    modelByProvider: { ...config.modelByProvider },
    providers,
    krakenVerifier: config.krakenVerifier ?? null,
    cliVersion: getCurrentVersion(),
    configPaths: {
      provider: getProviderConfigPath(),
      keys: getKeyStorePath(),
    },
  };
}

/** Write one JSON object to stdout (pretty for humans, still single payload). */
export function printDesktopConfig(): void {
  const snap = buildDesktopConfigSnapshot();
  process.stdout.write(JSON.stringify(snap, null, 2) + '\n');
}

/**
 * Persist provider, model, and/or custom endpoint.
 */
export function applySetConfig(
  req: SetConfigRequest,
): { ok: true; message: string } | { ok: false; error: string } {
  try {
    const config = getProviderConfig();
    let targetProvider = (req.provider ?? config.activeProviderId) as ProviderName;

    if (req.provider) {
      const exists = PROVIDERS.some((p) => p.id === req.provider);
      if (!exists) {
        return {
          ok: false,
          error: `unknown provider '${req.provider}'. Available: ${PROVIDERS.map((p) => p.id).join(', ')}`,
        };
      }
      setActiveProviderId(req.provider as ProviderName);
      targetProvider = req.provider as ProviderName;
    }

    // Endpoint ops target explicit provider or active; for endpoint-only
    // without --provider, prefer openai-compatible when clearing/setting
    // and user is not targeting a specific provider... actually use targetProvider.
    if (req.endpointClear) {
      clearCustomEndpoint(targetProvider);
    }
    if (req.endpoint) {
      setCustomEndpoint(targetProvider, req.endpoint);
    }

    if (req.apiStyle) {
      setApiStyleFor(targetProvider, req.apiStyle);
    }
    if (req.model) {
      setModelForProvider(targetProvider, req.model);
    }
    if (req.thinking) {
      setThinkingForProvider(targetProvider, parseThinkingSpec(req.thinking));
    }

    if (req.verifierClear) {
      clearKrakenVerifier();
    }
    if (req.verifierProvider && req.verifierModel) {
      const existsV = PROVIDERS.some((p) => p.id === req.verifierProvider);
      if (!existsV) {
        return {
          ok: false,
          error: `unknown verifier provider '${req.verifierProvider}'. Available: ${PROVIDERS.map((p) => p.id).join(', ')}`,
        };
      }
      setKrakenVerifier(req.verifierProvider, req.verifierModel);
    }

    const after = getProviderConfig();
    const vf = after.krakenVerifier;
    const ep = getCustomEndpoint(after.activeProviderId as ProviderName);
    return {
      ok: true,
      message:
        `activeProvider=${after.activeProviderId} ` +
        `model=${after.modelByProvider[after.activeProviderId]}` +
        (ep ? ` endpoint=${ep}` : '') +
        (vf ? ` verifier=${vf.provider}/${vf.model}` : ' verifier=inherit'),
    };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Store API key (never echoes the secret). */
export function applySetKey(
  req: SetKeyRequest,
): { ok: true; provider: string; masked: string } | { ok: false; error: string } {
  try {
    const exists = PROVIDERS.some((p) => p.id === req.provider);
    if (!exists) {
      return {
        ok: false,
        error: `unknown provider '${req.provider}'. Available: ${PROVIDERS.map((p) => p.id).join(', ')}`,
      };
    }
    setApiKey(req.provider, req.key);
    setActiveProviderId(req.provider as ProviderName);
    return {
      ok: true,
      provider: req.provider,
      masked: maskKey(req.key),
    };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

const DISCOVERABLE: DiscoveryProviderId[] = [
  'grok',
  'glm',
  'minimax',
  'deepseek',
  'openai-compatible',
  'chatgpt',
  'anthropic',
];

/** Fetch and cache models; print JSON result. */
export async function runDiscoverModels(
  providerArg: string | null,
): Promise<{ ok: true; payload: object } | { ok: false; error: string }> {
  const config = getProviderConfig();
  const providerId = (providerArg ?? config.activeProviderId).trim();

  if (!DISCOVERABLE.includes(providerId as DiscoveryProviderId)) {
    // custom may still work via openai-compatible discovery if mapped — try openai-compatible path
    if (providerId === 'custom') {
      // Fall through using openai-compatible discovery with custom endpoint if set
    } else {
      return {
        ok: false,
        error: `provider '${providerId}' is not discoverable. Use: ${DISCOVERABLE.join(', ')}`,
      };
    }
  }

  const discoveryId = (
    providerId === 'custom' ? 'openai-compatible' : providerId
  ) as DiscoveryProviderId;

  try {
    const entry = await discoverModelsForProvider(discoveryId);
    return {
      ok: true,
      payload: {
        ok: true,
        provider: providerId,
        models: entry.models.map((m) => m.id),
        fetchedAt: entry.fetchedAt,
        baseUrl: entry.baseUrl,
      },
    };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
